import React, { useState } from "react";
import { Wand2, Copy, Check, RefreshCw } from "lucide-react";
import { memorablePassword, randomPassword } from "../utils/passwordGen";
import { useLang } from "../context/LanguageContext";

// A tap-to-suggest password helper shown under the password field on the sign-up screens.
// The user flips between a memorable passphrase and a strong random string, then uses or copies it.
export const PasswordGenerator = ({ onPick, testid = "pw-gen" }) => {
  const { t } = useLang();
  const [mode, setMode] = useState("memorable");
  const [words, setWords] = useState(3);
  const [value, setValue] = useState("");
  const [copied, setCopied] = useState(false);

  const make = (m = mode, n = words) => {
    const pw = m === "memorable" ? memorablePassword(n) : randomPassword();
    setValue(pw);
    setCopied(false);
    onPick?.(pw);
  };

  const switchMode = (m) => { setMode(m); make(m, words); };
  const switchWords = (n) => { setWords(n); make("memorable", n); };

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — the value is already visible */ }
  };

  const chip = (m, label) => (
    <button type="button" onClick={() => switchMode(m)} data-testid={`${testid}-mode-${m}`}
      className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${mode === m
        ? "bg-[#16a34a] text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
      {label}
    </button>
  );

  return (
    <div className="mt-2 rounded-xl border border-dashed border-border bg-secondary/40 p-3" data-testid={testid}>
      {!value ? (
        <button type="button" onClick={() => make()} data-testid={`${testid}-start`}
          className="flex items-center gap-2 text-sm font-semibold text-[#16a34a] hover:underline">
          <Wand2 size={15} /> {t("pw.suggest")}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            {chip("memorable", t("pw.memorable"))}
            {chip("random", t("pw.random"))}
          </div>
          {mode === "memorable" && (
            <div className="flex items-center gap-2" data-testid={`${testid}-words`}>
              <span className="text-[11px] font-semibold text-muted-foreground">{t("pw.words")}</span>
              <div className="flex gap-1">
                {[3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => switchWords(n)} data-testid={`${testid}-words-${n}`}
                    className={`h-6 w-6 rounded-md text-[11px] font-bold transition-colors ${words === n
                      ? "bg-[#16a34a] text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 rounded-lg bg-background border border-border px-3 py-2">
            <code className="flex-1 text-sm font-mono text-foreground break-all" data-testid={`${testid}-value`}>{value}</code>
            <button type="button" onClick={() => make()} aria-label={t("pw.reroll")} data-testid={`${testid}-reroll`}
              className="-m-1 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"><RefreshCw size={15} /></button>
            <button type="button" onClick={copy} aria-label={t("pw.copy")} data-testid={`${testid}-copy`}
              className="-m-1 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0">
              {copied ? <Check size={15} className="text-[#16a34a]" /> : <Copy size={15} />}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">{t("pw.suggestNote")}</p>
        </div>
      )}
    </div>
  );
};

export default PasswordGenerator;
